import type { DocumentNode, FragmentDefinitionNode } from 'graphql';
import { isNull } from 'lodash';
import { useFragment } from '@frameio/graphql-client';
import type {
  Share,
  SharePermissions,
} from '@frameio/graphql-client/generated';
import {
  CanCreateInternalCommentsPermissionForShare,
  CanViewInternalCommentsPermissionForShare,
} from '@frameio/graphql-client/generated';

type UseProjectDerivedSharePermissionsArgs = {
  /** The permission field to check */
  check: keyof Omit<SharePermissions, '__typename'>;
  /** Whether to consider the permission true initially prior to data resolving */
  isInitiallyPermitted?: boolean;
  /** The id of the Share to check against */
  shareId: Share['id'];
};

/**
 * Reactive hook to read Project derived Share permissions directly off the cache
 * @param {check, isInitiallyPermitted, projectId}
 * @returns A boolean of whether a given check for the Share with the given `shareId` is true
 */
export const useProjectDerivedSharePermissions = ({
  check,
  isInitiallyPermitted = false,
  shareId,
}: UseProjectDerivedSharePermissionsArgs): boolean | null => {
  let fragment: DocumentNode;
  switch (check) {
    case 'canCreateInternalComments':
      fragment = CanCreateInternalCommentsPermissionForShare;
      break;
    case 'canViewInternalComments':
      fragment = CanViewInternalCommentsPermissionForShare;
      break;
    default:
      break;
  }

  const fragmentNode = fragment.definitions[0] as FragmentDefinitionNode;

  const result = useFragment({
    fragment,
    fragmentName: fragmentNode.name.value,
    from: {
      __typename: 'Share',
      id: shareId,
    },
  });

  // If the permission does not exist yet, we want to check against that rather than defaulting to false
  const isPermissionUnresolved =
    isNull(result) ||
    Object.keys(result?.data)?.length < 1 ||
    !result?.complete;
  /**
   * Return the result of the fragment, and fall back to the prop that controls whether we consider the permission true
   * prior to the permission being resolved
   */
  return isPermissionUnresolved
    ? isInitiallyPermitted
    : result.data?.permissions?.[check];
};
